/**
 * @file 学员管理主面板
 * @description RequestTable + 本地 mock；可折叠筛选；分科目展平列表。
 *
 * @whenToUse 仅由 StudentPage 组合。
 * @whenNotToUse 其他业务列表页。
 */

import { useState } from 'react'
import { MoreHorizontal, Pencil, Plus } from 'lucide-react'
import { confirm } from '@/components/ui/confirm.tsx'
import { Badge, type BadgeVariant } from '@/components/ui/badge.tsx'
import { Button } from '@/components/ui/button.tsx'
import {
  computeConsecutiveRowSpan,
  type DataTableColumn,
} from '@/components/ui/data-table.tsx'
import { DropdownMenu } from '@/components/ui/dropdown-menu.tsx'
import { IconButton } from '@/components/ui/icon-button.tsx'
import { PageHeader } from '@/components/ui/page-header.tsx'
import { RequestTable } from '@/components/ui/request-table.tsx'
import { toast } from '@/components/ui/toast.tsx'
import { StudentBindTeacherDialog } from '@/components/pages/student-page/student-bind-teacher-dialog.tsx'
import { StudentFormDialog } from '@/components/pages/student-page/student-form-dialog.tsx'
import {
  StudentPointsDialog,
  type StudentPointsDialogMode,
} from '@/components/pages/student-page/student-points-dialog.tsx'
import { StudentSearchFields } from '@/components/pages/student-page/student-search-fields.tsx'
import {
  EMPTY_STUDENT_QUERY,
  GRADE_OPTIONS,
  LEARNING_LEVEL_OPTIONS,
  MOCK_STUDENTS,
  SEMESTER_OPTIONS,
  STAGE_OPTIONS,
  STUDENT_STATUS_LABEL,
  SUBJECT_OPTIONS,
  TEXTBOOK_OPTIONS,
  filterStudentListRows,
  flattenStudentRows,
  optionLabel,
  type StudentFormValues,
  type StudentListRow,
  type StudentQuery,
  type StudentRow,
  type StudentStatus,
} from '@/components/pages/student-page/mock.ts'
import { usePermissionSimStore } from '@/stores/permission-sim-store.ts'

const STATUS_BADGE: Record<StudentStatus, BadgeVariant> = {
  normal: 'success',
  trial: 'warning',
  disabled: 'danger',
}

/**
 * 格式化本地时间字符串
 * @param date 时间
 */
function formatDateTime(date: Date): string {
  const pad = (n: number) => String(n).padStart(2, '0')
  return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())} ${pad(date.getHours())}:${pad(date.getMinutes())}:${pad(date.getSeconds())}`
}

/**
 * 学员管理面板
 */
export function StudentPanel() {
  const isMainAccount = usePermissionSimStore((s) => s.role) === 'main'
  const [rows, setRows] = useState<StudentRow[]>(MOCK_STUDENTS)
  const [formOpen, setFormOpen] = useState(false)
  const [editing, setEditing] = useState<StudentRow | null>(null)
  const [pointsOpen, setPointsOpen] = useState(false)
  const [pointsMode, setPointsMode] =
    useState<StudentPointsDialogMode>('recharge')
  const [pointsTarget, setPointsTarget] = useState<StudentRow | null>(null)
  const [bindOpen, setBindOpen] = useState(false)
  const [bindTarget, setBindTarget] = useState<StudentRow | null>(null)
  const [bindDetailId, setBindDetailId] = useState<string | undefined>()

  const listData = flattenStudentRows(rows)

  const openCreate = () => {
    setEditing(null)
    setFormOpen(true)
  }

  const openEdit = (studentId: string) => {
    const student = rows.find((item) => item.id === studentId)
    if (!student) return
    setEditing(student)
    setFormOpen(true)
  }

  /**
   * 打开充值 / 扣除弹窗
   * @param studentId 学员 id
   * @param mode 充值或扣除
   */
  const openPointsDialog = (
    studentId: string,
    mode: StudentPointsDialogMode,
  ) => {
    const student = rows.find((item) => item.id === studentId)
    if (!student) return
    setPointsTarget(student)
    setPointsMode(mode)
    setPointsOpen(true)
  }

  /**
   * 确认充值 / 扣除
   * @param points 变动点数（正数）
   * @param _remark 备注（本地暂存未落库）
   */
  const handlePointsConfirm = (points: number, _remark: string) => {
    if (!pointsTarget) return
    const studentId = pointsTarget.id
    const delta = pointsMode === 'deduct' ? -points : points
    setRows((prev) =>
      prev.map((row) =>
        row.id === studentId ? { ...row, points: row.points + delta } : row,
      ),
    )
    toast.success(
      pointsMode === 'deduct' ? '扣除成功' : '充值成功',
      `${delta > 0 ? '+' : ''}${delta} 点`,
    )
  }

  /**
   * 打开修改老师弹窗（按科目详情）
   * @param studentId 学员 id
   * @param detailId 科目详情 id
   */
  const openEditTeacherDialog = (studentId: string, detailId: string) => {
    const student = rows.find((item) => item.id === studentId)
    if (!student) return
    setBindTarget(student)
    setBindDetailId(detailId)
    setBindOpen(true)
  }

  /**
   * 确认绑定 / 修改老师
   * @param teacherName 教师姓名
   */
  const handleBindTeacherConfirm = (teacherName: string) => {
    if (!bindTarget) return
    const studentId = bindTarget.id
    const detailId = bindDetailId
    setRows((prev) =>
      prev.map((row) => {
        if (row.id !== studentId) return row
        return {
          ...row,
          details: row.details.map((detail) => {
            if (detailId && detail.id !== detailId) return detail
            return { ...detail, teacherName }
          }),
        }
      }),
    )
    toast.success(detailId ? '已修改老师' : '已绑定老师', teacherName)
  }

  const handleFormSubmit = (values: StudentFormValues) => {
    const details = values.details.map((item, index) => ({
      id: item.key.startsWith('d-') ? `${Date.now()}-${index}` : item.key,
      subject: item.subject,
      textbook: item.textbook,
      grade: item.grade,
      semester: item.semester,
      stage: item.stage,
      learningLevel: item.learningLevel,
      learningPower: item.learningPower,
      teacherName: item.teacherName,
    }))

    if (editing) {
      setRows((prev) =>
        prev.map((row) =>
          row.id === editing.id
            ? {
                ...row,
                name: values.name,
                phone: values.phone,
                points: values.points,
                details,
              }
            : row,
        ),
      )
      toast.success('已保存学员')
      return
    }

    const id = `stu-${Date.now()}`
    setRows((prev) => [
      {
        id,
        name: values.name,
        phone: values.phone,
        points: values.points,
        status: 'normal',
        createdAt: formatDateTime(new Date()),
        allowEditBasicInfo: false,
        details,
      },
      ...prev,
    ])
    toast.success('已新增学员')
  }

  const handleDelete = async (studentId: string, name: string) => {
    const ok = await confirm({
      title: '删除学员？',
      description: `将删除「${name}」，此操作不可恢复。`,
      confirmText: '删除',
      danger: true,
    })
    if (!ok) return
    setRows((prev) => prev.filter((item) => item.id !== studentId))
    toast.success('已删除', name)
  }

  /**
   * 标记重新测试（二次确认）
   * @param name 学员名称
   */
  const handleRetest = async (name: string) => {
    const ok = await confirm({
      title: '重新测试？',
      description: `确认将「${name}」标记为需重新测试？`,
      confirmText: '确认',
    })
    if (!ok) return
    toast.success('已标记需重新测试', name)
  }

  /**
   * 允许 / 禁止调整基本信息（二次确认）
   * @param studentId 学员 id
   * @param name 学员名称
   * @param allowEditBasicInfo 当前是否允许
   */
  const handleToggleBasicInfo = async (
    studentId: string,
    name: string,
    allowEditBasicInfo: boolean,
  ) => {
    const willAllow = !allowEditBasicInfo
    const ok = await confirm({
      title: willAllow ? '允许调整基本信息？' : '禁止调整基本信息？',
      description: willAllow
        ? `确认允许「${name}」在 PAD 端调整基本信息？`
        : `确认禁止「${name}」在 PAD 端调整基本信息？`,
      confirmText: willAllow ? '允许' : '禁止',
    })
    if (!ok) return
    setRows((prev) =>
      prev.map((item) =>
        item.id === studentId
          ? { ...item, allowEditBasicInfo: willAllow }
          : item,
      ),
    )
    toast.success(
      willAllow ? '已允许调整基本信息' : '已禁止调整基本信息',
      name,
    )
  }

  /**
   * 启用 / 禁用学员（二次确认）
   * @param studentId 学员 id
   * @param name 学员名称
   * @param status 当前状态
   */
  const handleToggleStatus = async (
    studentId: string,
    name: string,
    status: StudentStatus,
  ) => {
    const willEnable = status === 'disabled'
    const ok = await confirm({
      title: willEnable ? '启用学员？' : '禁用学员？',
      description: willEnable
        ? `确认启用「${name}」？启用后可正常使用。`
        : `确认禁用「${name}」？禁用后将无法正常使用。`,
      confirmText: willEnable ? '启用' : '禁用',
      danger: !willEnable,
    })
    if (!ok) return

    const next: StudentStatus = willEnable ? 'normal' : 'disabled'
    setRows((prev) =>
      prev.map((item) =>
        item.id === studentId ? { ...item, status: next } : item,
      ),
    )
    toast.success(willEnable ? '已启用' : '已禁用', name)
  }

  /** 同一学员多科目行：学员维度列纵向合并 */
  const studentRowSpan: DataTableColumn<StudentListRow>['onCell'] = (
    _record,
    index,
    data,
  ) => ({
    rowSpan: computeConsecutiveRowSpan(
      data,
      index,
      (row) => row.studentId,
    ),
  })

  const columns: DataTableColumn<StudentListRow>[] = [
    {
      key: 'name',
      title: '学员名称',
      dataIndex: 'name',
      minWidth: 100,
      onCell: studentRowSpan,
    },
    {
      key: 'phone',
      title: '手机号',
      dataIndex: 'phone',
      minWidth: 120,
      onCell: studentRowSpan,
    },
    {
      key: 'points',
      title: '点数',
      dataIndex: 'points',
      minWidth: 72,
      onCell: studentRowSpan,
    },
    {
      key: 'status',
      title: '状态',
      minWidth: 88,
      onCell: studentRowSpan,
      render: (row) => (
        <Badge variant={STATUS_BADGE[row.status]}>
          {STUDENT_STATUS_LABEL[row.status]}
        </Badge>
      ),
    },
    {
      key: 'subject',
      title: '科目',
      minWidth: 72,
      className: 'border-l border-border',
      render: (row) => optionLabel(SUBJECT_OPTIONS, row.subject),
    },
    {
      key: 'textbook',
      title: '教材',
      minWidth: 120,
      render: (row) => optionLabel(TEXTBOOK_OPTIONS, row.textbook),
    },
    {
      key: 'grade',
      title: '年级',
      minWidth: 72,
      render: (row) => optionLabel(GRADE_OPTIONS, row.grade),
    },
    {
      key: 'semester',
      title: '学期',
      minWidth: 72,
      render: (row) => optionLabel(SEMESTER_OPTIONS, row.semester),
    },
    {
      key: 'stage',
      title: '阶段',
      minWidth: 72,
      render: (row) => optionLabel(STAGE_OPTIONS, row.stage),
    },
    {
      key: 'learningLevel',
      title: '学习力等级',
      minWidth: 96,
      render: (row) => optionLabel(LEARNING_LEVEL_OPTIONS, row.learningLevel),
    },
    {
      key: 'learningPower',
      title: '学习力',
      dataIndex: 'learningPower',
      minWidth: 72,
    },
    {
      key: 'teacherName',
      title: '所属教师',
      minWidth: 120,
      render: (row) => (
        <button
          type="button"
          className="inline-flex max-w-full items-center gap-1.5 text-left transition-opacity duration-150 hover:text-muted"
          aria-label={`修改${row.name}的老师`}
          onClick={() => openEditTeacherDialog(row.studentId, row.id)}
        >
          <Pencil className="size-3.5 shrink-0 text-muted" aria-hidden />
          <span className="truncate">{row.teacherName || '未绑定'}</span>
        </button>
      ),
    },
    {
      key: 'createdAt',
      title: '创建时间',
      dataIndex: 'createdAt',
      minWidth: 168,
      className: 'border-l border-border',
      onCell: studentRowSpan,
    },
    {
      key: 'allowEditBasicInfo',
      title: '是否允许调整基本信息',
      minWidth: 72,
      onCell: studentRowSpan,
      render: (row) => (
        <Badge variant={row.allowEditBasicInfo ? 'success' : 'danger'}>
          {row.allowEditBasicInfo ? '允许' : '禁止'}
        </Badge>
      ),
    },
    {
      key: 'actions',
      title: '操作',
      fixed: 'right',
      width: 72,
      onCell: studentRowSpan,
      render: (row) => (
        <DropdownMenu
          align="end"
          trigger={
            <IconButton
              variant="ghost"
              aria-label={`${row.name} 操作`}
              icon={<MoreHorizontal className="size-4" aria-hidden />}
            />
          }
          items={[
            {
              key: 'edit',
              label: '编辑',
              onSelect: () => openEdit(row.studentId),
            },
            {
              key: 'status',
              label: row.status === 'disabled' ? '启用' : '禁用',
              danger: row.status !== 'disabled',
              onSelect: () => {
                void handleToggleStatus(row.studentId, row.name, row.status)
              },
            },
            { key: 'div-1', type: 'divider' },
            {
              key: 'recharge',
              label: '充值',
              onSelect: () => openPointsDialog(row.studentId, 'recharge'),
            },
            {
              key: 'deduct',
              label: '扣除',
              disabled: row.points <= 100,
              onSelect: () => openPointsDialog(row.studentId, 'deduct'),
            },
            {
              key: 'retest',
              label: '重新测试',
              onSelect: () => {
                void handleRetest(row.name)
              },
            },
            {
              key: 'basic',
              label: row.allowEditBasicInfo
                ? '禁止调整基本信息'
                : '允许调整基本信息',
              disabled: !isMainAccount,
              onSelect: () => {
                void handleToggleBasicInfo(
                  row.studentId,
                  row.name,
                  row.allowEditBasicInfo,
                )
              },
            },
            { key: 'div-2', type: 'divider' },
            {
              key: 'delete',
              label: '删除',
              danger: true,
              onSelect: () => {
                void handleDelete(row.studentId, row.name)
              },
            },
          ]}
        />
      ),
    },
  ]

  return (
    <>
      <PageHeader
        title="学员管理"
        description="管理学员档案、点数与分科目学习信息。"
        extra={
          <Button
            icon={<Plus className="size-4" aria-hidden />}
            onClick={openCreate}
          >
            新增学员
          </Button>
        }
      />

      <div className="mt-6">
        <RequestTable<StudentListRow, StudentQuery>
          data={listData}
          filterData={filterStudentListRows}
          pageSize={10}
          columns={columns}
          searchForm={{
            initialValues: EMPTY_STUDENT_QUERY,
            collapsible: true,
            defaultExpanded: false,
            items: StudentSearchFields,
          }}
        />
      </div>

      {/* 新增 / 编辑学员弹窗 */}
      <StudentFormDialog
        open={formOpen}
        onOpenChange={setFormOpen}
        editing={editing}
        onSubmit={handleFormSubmit}
      />

      {/* 充值 / 扣除点数弹窗 */}
      <StudentPointsDialog
        open={pointsOpen}
        onOpenChange={setPointsOpen}
        student={pointsTarget}
        mode={pointsMode}
        onConfirm={handlePointsConfirm}
      />

      <StudentBindTeacherDialog
        open={bindOpen}
        onOpenChange={setBindOpen}
        student={bindTarget}
        detailId={bindDetailId}
        onConfirm={handleBindTeacherConfirm}
      />
    </>
  )
}
